import type {
  CostLimitSetting,
  CostLimitsListResponse,
  GetAdminTaskDetailResponse,
  GetAdminUsageResponse,
  GetAdminUserUsageResponse,
  ListAdminTasksResponse,
  ListAdminTaskVersionsResponse,
  ListAdminUsersResponse,
  UserCostLimitDetailResponse,
} from '@kyumo/common';
import {
  CostLimitSettingSchema,
  CostLimitsListResponseSchema,
  GetAdminTaskDetailResponseSchema,
  GetAdminUsageResponseSchema,
  GetAdminUserUsageResponseSchema,
  ListAdminTasksResponseSchema,
  ListAdminTaskVersionsResponseSchema,
  ListAdminUsersResponseSchema,
  UserCostLimitDetailResponseSchema,
} from '@kyumo/common';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * 認証トークンを取得
 */
async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken?.toString();
  if (!idToken) {
    throw new Error('認証トークンが取得できません');
  }
  return idToken;
}

/**
 * API リクエストを実行
 */
async function apiRequest<T>(path: string, params?: Record<string, string>): Promise<T> {
  const token = await getAuthToken();

  // API_URLの末尾スラッシュとpathの先頭スラッシュの重複を防ぐ
  const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
  const url = new URL(`${baseUrl}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, value);
      }
    }
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `API error: ${response.status}`);
  }

  return response.json();
}

/**
 * 書き込み系 API リクエストを実行（PUT / DELETE）
 */
async function apiMutationRequest<T>(
  path: string,
  method: 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  const token = await getAuthToken();

  const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
  const url = `${baseUrl}${path}`;

  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error?.message || `API error: ${response.status}`);
  }

  // 204 No Content or empty body
  const text = await response.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
}

// ==================== API 関数 ====================

/**
 * 使用量データを取得
 */
export async function fetchUsageData(
  month?: string,
  model?: string,
): Promise<GetAdminUsageResponse> {
  const params: Record<string, string> = {};
  if (month) params.month = month;
  if (model) params.model = model;

  const data = await apiRequest('/admin/usage', params);
  return GetAdminUsageResponseSchema.parse(data) as GetAdminUsageResponse;
}

/**
 * ユーザー別使用量データを取得
 */
export async function fetchUserUsageData(
  email: string,
  month?: string,
  model?: string,
): Promise<GetAdminUserUsageResponse> {
  const params: Record<string, string> = {};
  if (month) params.month = month;
  if (model) params.model = model;

  const data = await apiRequest(`/admin/usage/user/${encodeURIComponent(email)}`, params);
  return GetAdminUserUsageResponseSchema.parse(data) as GetAdminUserUsageResponse;
}

/**
 * ユーザー一覧を取得（全件）
 */
export async function fetchUsers(): Promise<ListAdminUsersResponse> {
  const data = await apiRequest('/admin/users');
  return ListAdminUsersResponseSchema.parse(data) as ListAdminUsersResponse;
}

/**
 * タスク一覧を取得（全件）
 */
export async function fetchTasks(email?: string): Promise<ListAdminTasksResponse> {
  const params: Record<string, string> = {};
  if (email) params.email = email;

  const data = await apiRequest('/admin/tasks', params);
  return ListAdminTasksResponseSchema.parse(data) as ListAdminTasksResponse;
}

/**
 * タスク詳細を取得
 */
export async function fetchTaskDetail(
  email: string,
  taskId: string,
): Promise<GetAdminTaskDetailResponse> {
  const data = await apiRequest(
    `/admin/users/${encodeURIComponent(email)}/tasks/${encodeURIComponent(taskId)}`,
  );
  return GetAdminTaskDetailResponseSchema.parse(data) as GetAdminTaskDetailResponse;
}

/**
 * タスクバージョン一覧を取得
 */
export async function fetchTaskVersions(
  email: string,
  taskId: string,
): Promise<ListAdminTaskVersionsResponse> {
  const data = await apiRequest(
    `/admin/users/${encodeURIComponent(email)}/tasks/${encodeURIComponent(taskId)}/versions`,
  );
  return ListAdminTaskVersionsResponseSchema.parse(data) as ListAdminTaskVersionsResponse;
}

/**
 * 特定バージョンのタスク詳細を取得
 */
export async function fetchTaskVersion(
  email: string,
  taskId: string,
  version: number,
): Promise<GetAdminTaskDetailResponse> {
  const data = await apiRequest(
    `/admin/users/${encodeURIComponent(email)}/tasks/${encodeURIComponent(taskId)}/versions/${version}`,
  );
  return GetAdminTaskDetailResponseSchema.parse(data) as GetAdminTaskDetailResponse;
}

/**
 * ファイルダウンロード用の署名付きURLを取得
 */
export async function fetchDownloadUrl(s3Key: string): Promise<{ downloadUrl: string }> {
  return apiRequest<{ downloadUrl: string }>('/files/download-url', { s3Key });
}

// ==================== コスト上限 API ====================

/**
 * コスト上限一覧を取得（デフォルト上限 + 全ユーザー個別上限）
 */
export async function fetchCostLimits(): Promise<CostLimitsListResponse> {
  const data = await apiRequest('/admin/cost-limits');
  return CostLimitsListResponseSchema.parse(data) as CostLimitsListResponse;
}

/**
 * デフォルトコスト上限を取得
 */
export async function fetchDefaultCostLimit(): Promise<CostLimitSetting | null> {
  const data = await apiRequest('/admin/cost-limits/default');
  return CostLimitSettingSchema.nullable().parse(data);
}

/**
 * デフォルトコスト上限を設定
 */
export async function setDefaultCostLimit(limitUSD: number): Promise<CostLimitSetting> {
  const data = await apiMutationRequest('/admin/cost-limits/default', 'PUT', { limitUSD });
  return CostLimitSettingSchema.parse(data);
}

/**
 * デフォルトコスト上限を削除
 */
export async function deleteDefaultCostLimit(): Promise<void> {
  await apiMutationRequest('/admin/cost-limits/default', 'DELETE');
}

/**
 * ユーザー個別コスト上限詳細を取得
 */
export async function fetchUserCostLimitDetail(
  email: string,
): Promise<UserCostLimitDetailResponse> {
  const data = await apiRequest(`/admin/cost-limits/users/${encodeURIComponent(email)}`);
  return UserCostLimitDetailResponseSchema.parse(data) as UserCostLimitDetailResponse;
}

/**
 * ユーザー個別コスト上限を設定
 */
export async function setUserCostLimit(email: string, limitUSD: number): Promise<CostLimitSetting> {
  const data = await apiMutationRequest(
    `/admin/cost-limits/users/${encodeURIComponent(email)}`,
    'PUT',
    { limitUSD },
  );
  return CostLimitSettingSchema.parse(data);
}

/**
 * ユーザー個別コスト上限を削除
 */
export async function deleteUserCostLimit(email: string): Promise<void> {
  await apiMutationRequest(`/admin/cost-limits/users/${encodeURIComponent(email)}`, 'DELETE');
}
